"use client";

import { Clock, Mail, MapPin, Phone } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { CONTACT, type PhoneNumber } from "@/lib/constants/contact";
import { cn } from "@/lib/utils";

interface ContactChannelsProps {
  className?: string;
}

/**
 * The ways to reach Miller40, beside the form rather than two scrolls below it.
 *
 * Each row takes its visible text and its href from separate fields of one
 * CONTACT.phones entry: `display` is the brand number customers recognize,
 * `telHref` dials the line net2phone tracks. See lib/constants/contact.ts. The
 * collision shop has no tracked equivalent, so its display and dial are the
 * same number — but it is still sourced from CONTACT, never inlined.
 *
 * Icons are outlined neutral, not filled yellow: yellow belongs to the submit
 * button, and three yellow discs here would compete with it.
 */
export function ContactChannels({ className }: ContactChannelsProps) {
  const t = useTranslations("contactInfo");

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <Channel
        icon={<Mail aria-hidden="true" className="h-5 w-5" />}
        title={t("email.title")}
      >
        <a
          href="mailto:info@miller40.com"
          className="text-yellow-500 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-site-card"
        >
          info@miller40.com
        </a>
      </Channel>

      <Channel
        icon={<Phone aria-hidden="true" className="h-5 w-5" />}
        title={t("call.title")}
      >
        <PhoneLink
          label={t("call.mechanicShop")}
          phone={CONTACT.phones.primary}
        />
        <PhoneLink
          label={t("call.collisionShop")}
          phone={CONTACT.phones.collision}
        />
        <PhoneLink
          label={t("call.parts")}
          phone={CONTACT.phones.parts}
        />
      </Channel>

      <Channel
        icon={<MapPin aria-hidden="true" className="h-5 w-5" />}
        title={t("visit.title")}
      >
        <Link
          href="/locations"
          className="text-yellow-500 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-site-card"
        >
          {t("visit.actionText")}
        </Link>
      </Channel>

      <Channel
        icon={<Clock aria-hidden="true" className="h-5 w-5" />}
        title={t("hoursLabel")}
      >
        <p className="text-site-foreground/80">{t("hoursValue")}</p>
      </Channel>
    </div>
  );
}

function Channel({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4">
      <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 text-site-foreground/80">
        {icon}
      </span>
      <div className="min-w-0">
        <h3 className="mb-1 text-base font-semibold text-site-foreground">
          {title}
        </h3>
        <div className="flex flex-col gap-1 text-sm">{children}</div>
      </div>
    </div>
  );
}

/**
 * The single, reusable way to render a tracked phone anchor. Takes a whole
 * PhoneNumber and emits its href, data-phone and display from that one object,
 * so a call site physically cannot mix a display string into a tel: href
 * (WSP-832). Exported so every surface can share it instead of hand-rolling the
 * anchor. `label` is optional: when present it names the line (for two numbers
 * side by side) and is folded into the accessible name; when absent the number
 * itself is the accessible name.
 */
export function PhoneLink({
  label,
  phone,
}: {
  label?: string;
  phone: PhoneNumber;
}) {
  return (
    <span className="flex flex-wrap items-baseline gap-x-2">
      {label && <span className="text-site-foreground/60">{label}</span>}
      <a
        href={phone.telHref}
        data-phone={phone.key}
        aria-label={label ? `${label}: ${phone.display}` : phone.display}
        className="text-yellow-500 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-site-card"
      >
        {phone.display}
      </a>
    </span>
  );
}
